import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import {
  Assignment,
  useDeleteAssignmentMutation,
} from 'shared/api/assignment-service';
import { ASSIGNMENT_UPDATED_EVENT, RealtimeService } from 'shared/lib/realtime';
import { useAppDispatch } from 'shared/lib/redux';
import { ScheduledAssignmentCard } from 'entities/assignments';
import { useClassData } from 'entities/classes';
import {
  getAssignmentsErrorMessages,
  setAssignmentActionErrorMessage,
  setEditingAssignment,
  setIsAssignmentActionNotificationActive,
  setIsAssignmentPanelOpened,
  useAssignmentActionTimestampHandlers,
} from 'features/assignment-panel';

import styles from './styles.module.scss';

export type ScheduledAssignmentsContentProps = {
  assignments: Assignment[] | null | undefined;
};

export const ScheduledAssignmentsList = ({
  assignments,
}: ScheduledAssignmentsContentProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const ASSIGNMENTS_ERROR_MESSAGE = getAssignmentsErrorMessages(t);

  const { writeAssignmentActionTimestamp } =
    useAssignmentActionTimestampHandlers();

  const [deleteAssignment] = useDeleteAssignmentMutation();

  const { classData } = useClassData();

  const handleEditBtnClick = (assignment: Assignment) => {
    dispatch(setEditingAssignment(assignment));
    dispatch(setIsAssignmentPanelOpened(true));
  };

  const handleUnassignBtnClick = (assignment: Assignment) => {
    // Store assignment that's being deleted to be able to undo this action.
    dispatch(setEditingAssignment(assignment));

    const assignmentKey = assignment.businessKey;

    // reset error message before each request to apply correct styles to notification popup
    dispatch(setAssignmentActionErrorMessage(null));

    deleteAssignment(assignmentKey)
      .unwrap()
      .then(() => {
        writeAssignmentActionTimestamp();

        RealtimeService.sendRealtimeEvent(
          assignment.classBks[0],
          ASSIGNMENT_UPDATED_EVENT,
          {
            assignment,
            startDate: assignment.startDate,
            dueDate: assignment.dueDate,
            isDeleted: true,
          },
        );
      })
      .catch(() => {
        dispatch(
          setAssignmentActionErrorMessage(ASSIGNMENTS_ERROR_MESSAGE.UNASSIGN),
        );
      })
      .finally(() => {
        // Show "Undo assignment action" notification.
        dispatch(setIsAssignmentActionNotificationActive(true));
      });
  };

  return (
    <div
      className={clsx(
        'd-flex flex-wrap justify-content-between align-items-stretch position-relative',
        styles.listContainer,
      )}
    >
      {assignments?.map((assignment) => {
        const onEditBtnClick = () => handleEditBtnClick(assignment);
        const onUnassignBtnClick = () => handleUnassignBtnClick(assignment);

        const assignmentClassBk = assignment.classBks[0];
        const assignmentClassGrades =
          classData?.[assignmentClassBk]?.grades ?? [];
        const assignmentClassLabel =
          classData?.[assignmentClassBk]?.displayName ?? '';

        return (
          <div
            className={styles.cardContainer}
            key={assignment.businessKey}
            data-testid="scheduled-assignment"
          >
            <ScheduledAssignmentCard
              assignment={assignment}
              assignmentClassLabel={assignmentClassLabel}
              currentClassGrades={assignmentClassGrades}
              onEditBtnClick={onEditBtnClick}
              onUnassignBtnClick={onUnassignBtnClick}
            />
          </div>
        );
      })}
    </div>
  );
};
